'use client';

/**
 * GitHub-style activity heatmap for a user profile.
 *
 * Cells are 12px squares colored by activity intensity (5 buckets, 0 = empty).
 * The grid is 53 columns (weeks) × 7 rows (days), with month labels along
 * the top and weekday labels on the left. Hover any cell → tooltip with
 * the exact date + event count.
 *
 * Doesn't use Canvas — CSS grid renders fine at ~53×7 = 371 cells.
 */

import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { ActivityPoint } from '@/types';

type Props = { username: string };

const WEEKDAY_LABELS = ['', 'Mon', '', 'Wed', '', 'Fri', ''];
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function bucketFor(count: number, max: number): number {
  if (count <= 0) return 0;
  if (max <= 0) return 0;
  const ratio = count / max;
  if (ratio > 0.75) return 4;
  if (ratio > 0.5) return 3;
  if (ratio > 0.25) return 2;
  return 1;
}

// 5 levels — empty → dimmest → brightest. Driven by `--accent` so the
// heatmap inherits whatever the user picked in the accent dropdown.
const BUCKET_COLORS = [
  'var(--bg-soft)',
  'color-mix(in oklch, var(--accent) 25%, var(--bg-raised))',
  'color-mix(in oklch, var(--accent) 45%, var(--bg-raised))',
  'color-mix(in oklch, var(--accent) 65%, var(--bg-raised))',
  'color-mix(in oklch, var(--accent) 85%, var(--bg-raised))',
];

export default function ActivityHeatmap({ username }: Props) {
  const [data, setData] = useState<{ total: number; series: ActivityPoint[] } | null>(null);

  useEffect(() => {
    api.users.activity(username, 365)
      .then((d) => setData({ total: d.total, series: d.series }))
      .catch(() => setData(null));
  }, [username]);

  // Transform the series into a 2D grid (7 rows × ~53 cols).
  const { grid, max, monthMarkers } = useMemo(() => {
    if (!data) return { grid: [] as (ActivityPoint | null)[][], max: 0, monthMarkers: [] as { col: number; label: string }[] };

    // Align the first column so the top row is the Sunday of the oldest
    // week — matches GitHub's convention.
    const first = new Date(data.series[0]?.date + 'T00:00:00');
    const leadingBlanks = first.getDay(); // 0 = Sun
    const cells: (ActivityPoint | null)[] = [...Array(leadingBlanks).fill(null), ...data.series];
    const weeks: (ActivityPoint | null)[][] = [];
    for (let i = 0; i < cells.length; i += 7) {
      weeks.push(cells.slice(i, i + 7));
    }
    // Transpose so we iterate rows (day) outer, cols (week) inner.
    const rows: (ActivityPoint | null)[][] = Array.from({ length: 7 }, () => []);
    weeks.forEach((w) => {
      for (let r = 0; r < 7; r++) rows[r].push(w[r] ?? null);
    });

    const maxCount = Math.max(1, ...data.series.map((p) => p.count));

    // Month labels: mark the first week where a new month starts.
    const markers: { col: number; label: string }[] = [];
    weeks.forEach((w, colIdx) => {
      const firstFilled = w.find((c) => c !== null);
      if (!firstFilled) return;
      const d = new Date(firstFilled.date + 'T00:00:00');
      if (d.getDate() <= 7) markers.push({ col: colIdx, label: MONTH_LABELS[d.getMonth()] });
    });

    return { grid: rows, max: maxCount, monthMarkers: markers };
  }, [data]);

  if (!data) return null;

  return (
    <section style={{ marginTop: 36 }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{
          fontSize: 10, fontWeight: 600, letterSpacing: '0.2em',
          textTransform: 'uppercase', color: 'var(--accent)', marginBottom: 6,
        }}>
          Last 12 months
        </div>
        <h2 style={{
          fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 600,
          letterSpacing: '-0.02em', color: 'var(--ink)', margin: 0,
        }}>
          {data.total.toLocaleString()} activit{data.total === 1 ? 'y' : 'ies'}
        </h2>
      </div>

      <div style={{
        padding: 16,
        background: 'var(--bg-raised)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--radius-md)',
        overflowX: 'auto',
      }}>
        {/* Month labels */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: `30px repeat(${grid[0]?.length ?? 0}, 13px)`,
          gap: 3,
          marginBottom: 4,
          fontSize: 10,
          color: 'var(--ink-4)',
          minWidth: 'fit-content',
        }}>
          <div />
          {Array.from({ length: grid[0]?.length ?? 0 }, (_, c) => {
            const marker = monthMarkers.find((m) => m.col === c);
            return (
              <div key={c} style={{ textAlign: 'left', fontSize: 10, lineHeight: 1 }}>
                {marker?.label ?? ''}
              </div>
            );
          })}
        </div>

        {/* The grid itself */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 'fit-content' }}>
          {grid.map((row, r) => (
            <div key={r} style={{
              display: 'grid',
              gridTemplateColumns: `30px repeat(${row.length}, 13px)`,
              gap: 3,
              alignItems: 'center',
            }}>
              <div style={{ fontSize: 10, color: 'var(--ink-4)', lineHeight: 1 }}>
                {WEEKDAY_LABELS[r]}
              </div>
              {row.map((cell, c) => {
                const bucket = cell ? bucketFor(cell.count, max) : 0;
                return (
                  <div
                    key={c}
                    title={cell ? `${cell.date} · ${cell.count} event${cell.count === 1 ? '' : 's'}` : ''}
                    style={{
                      width: 10, height: 10, borderRadius: 2,
                      background: cell ? BUCKET_COLORS[bucket] : 'transparent',
                      border: cell && bucket === 0 ? '1px solid var(--line)' : 'none',
                      transition: 'transform 0.08s',
                    }}
                  />
                );
              })}
            </div>
          ))}
        </div>

        {/* Legend */}
        <div style={{
          marginTop: 10, display: 'flex', alignItems: 'center',
          justifyContent: 'flex-end', gap: 6,
          fontSize: 10, color: 'var(--ink-4)',
        }}>
          <span>Less</span>
          {BUCKET_COLORS.map((c, i) => (
            <div key={i} style={{
              width: 10, height: 10, borderRadius: 2, background: c,
              border: i === 0 ? '1px solid var(--line)' : 'none',
            }} />
          ))}
          <span>More</span>
        </div>
      </div>
    </section>
  );
}
